import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { logos } from '@/brand';

/** Campo de formulário com rótulo, dica e erro associados (acessível a leitores de tela). */
export function Field(
  props: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string },
) {
  const { label, hint, error, id, ...input } = props;
  const autoId = useId();
  const fieldId = id ?? autoId;
  const describedBy = [hint ? `${fieldId}-hint` : '', error ? `${fieldId}-error` : ''].filter(Boolean).join(' ');
  return (
    <div className="field">
      <label htmlFor={fieldId}>{label}</label>
      <input id={fieldId} aria-invalid={error ? true : undefined} aria-describedby={describedBy || undefined} {...input} />
      {hint && (
        <small id={`${fieldId}-hint`} className="field__hint">
          {hint}
        </small>
      )}
      {error && (
        <small id={`${fieldId}-error`} className="field__error" role="alert">
          {error}
        </small>
      )}
    </div>
  );
}

export function Button(
  props: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; loading?: boolean },
) {
  const { variant = 'primary', loading, children, disabled, className, ...rest } = props;
  return (
    <button
      className={`btn btn--${variant} ${className ?? ''}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? 'Aguarde…' : children}
    </button>
  );
}

export function Alert({ kind = 'error', children }: { kind?: 'error' | 'info' | 'success' | 'warning'; children: ReactNode }) {
  return (
    <div className={`alert alert--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

/** Página de acesso (login, 2FA, primeiro acesso): fundo azul-marinho com o símbolo "F". */
export function AuthLayout({ title, subtitle, step, children }: { title: string; subtitle?: ReactNode; step?: string; children: ReactNode }) {
  return (
    <main className="auth">
      <img src={logos.fClaro} alt="Fly Now Viagens" className="auth__logo" />
      <section className="auth__card" aria-labelledby="auth-title">
        {step && <span className="auth__step">{step}</span>}
        <h1 id="auth-title">{title}</h1>
        {subtitle && <p className="muted">{subtitle}</p>}
        {children}
      </section>
      <p className="auth__footer">Acesso restrito à equipe Fly Now Viagens.</p>
    </main>
  );
}

export function FullPageLoading() {
  return (
    <main className="auth" aria-busy="true">
      <img src={logos.fClaro} alt="" className="auth__spinner" />
      <p className="auth__footer">Carregando…</p>
    </main>
  );
}
